/**
 * @graphql cc
 */
import React, { memo, useCallback, useEffect, useMemo, useState, type FC } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import debounce from 'lodash/debounce';
import type { WatchQueryFetchPolicy } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useDocChangeContext } from '@confluence/editor-features/entry-points/useDocChangeContext.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { fg } from '@confluence/feature-gating';

import { ReadTimeQuery } from './ReadTimeQuery.graphql.ts';
import type {
	ReadTimeQuery as ReadTimeQueryType,
	ReadTimeQueryVariables,
} from './__types__/ReadTimeQuery.ts';
import { ReadTimePopupWrapper } from './ReadTimePopupWrapper.tsx';
import {
	calculateEstimatedReadTime,
	getAdf,
	getContentFeatureExtractedEventObject,
	getWordCount,
} from './readTimeUtils.ts';
import {
	extractContentFeatures,
	extractContentFeaturesSkipExpand,
	type ContentFeatures,
} from './featuresExtractor.ts';
import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';

type EditorReadTimeProps = {
	contentId: string;
	editorApi?: FullPageEditorPresetAPI | null;
	showWordCount?: boolean;
	isDetailsPanel?: boolean;
	isAbbreviatedReadTime?: boolean;
};

/**
 * Read Time component with support for automatically recalculating and updating read time after a document change in the editor.
 */
export const EditorReadTime: FC<EditorReadTimeProps> = memo(
	({
		contentId,
		editorApi,
		showWordCount,
		isDetailsPanel,
		isAbbreviatedReadTime = true,
	}: EditorReadTimeProps) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { subscribeDocChange, unsubscribeDocChange } = useDocChangeContext();
		const [features, setFeatures] = useState<ContentFeatures | null>(null);
		const [featuresSkipExpand, setFeaturesSkipExpand] = useState<ContentFeatures | null>(null);

		const isPreloadingEnabled =
			/* eslint-disable-next-line confluence-feature-gating/no-preconditioning */
			fg('cc_page_experiences_new_editor_byline') && fg('cc_ssr_preload_header_data_for_edit_page');

		const fetchPolicy: WatchQueryFetchPolicy = isPreloadingEnabled ? 'cache-only' : 'cache-first';

		const { data, loading, error } = useQuery<ReadTimeQueryType, ReadTimeQueryVariables>(
			// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
			ReadTimeQuery,
			{
				variables: {
					contentId,
					...(isPreloadingEnabled && { status: ['current', 'draft'] }),
				},
				fetchPolicy,
			},
		);

		const adf = useMemo(() => getAdf(data), [data]);

		// Initialize readtime with preloaded data for SSR
		const [readTime, setReadTime] = useState<number | null>(calculateEstimatedReadTime(adf));
		const [wordCount, setWordCount] = useState(getWordCount(adf));

		useEffect(() => {
			if (!createAnalyticsEvent || !adf || loading || error || !contentId) {
				return;
			}
			const contentType = data?.content?.nodes?.[0]?.type ?? '';
			createAnalyticsEvent(
				getContentFeatureExtractedEventObject({ contentType, contentId, adf, isInEditor: true }),
			).fire();
		}, [contentId, loading, adf, data?.content?.nodes, error, createAnalyticsEvent]);

		useEffect(() => {
			if (adf && !loading && !error) {
				setWordCount(getWordCount(adf));
				setReadTime(calculateEstimatedReadTime(adf));
			}
		}, [adf, loading, error]);

		const setReadTimeFromEditorState = useCallback(async () => {
			if (editorApi) {
				editorApi.core?.actions.requestDocument((doc) => {
					setWordCount(getWordCount(doc));
					setReadTime(calculateEstimatedReadTime(doc));
					if (doc) {
						setFeatures(extractContentFeatures(doc));
						setFeaturesSkipExpand(extractContentFeaturesSkipExpand(doc));
					}
				});
			}
		}, [editorApi]);

		useEffect(() => {
			// Sometimes the ADF from the query is outdated if the byline is re-rendered quickly after a document change
			// So recalculate read time from the editor ADF when the editor is ready
			void setReadTimeFromEditorState();
		}, [setReadTimeFromEditorState]);

		const debouncedCalculateReadTime = useMemo(
			() => debounce(setReadTimeFromEditorState, 5000),
			[setReadTimeFromEditorState],
		);

		useEffect(() => {
			subscribeDocChange(debouncedCalculateReadTime);
			return () => unsubscribeDocChange(debouncedCalculateReadTime);
		}, [debouncedCalculateReadTime, subscribeDocChange, unsubscribeDocChange]);

		if (error) {
			return <ErrorDisplay error={error} />;
		}
		if (readTime || (showWordCount && wordCount != null)) {
			return (
				<ReadTimePopupWrapper
					contentId={contentId}
					isAbbreviatedReadTime={isAbbreviatedReadTime}
					isDetailsPanel={isDetailsPanel}
					showWordCount={showWordCount}
					features={features}
					featuresSkipExpand={featuresSkipExpand}
					wordCount={wordCount}
					readTime={readTime}
					source="editorReadTime"
				/>
			);
		}
	},
);
